<template>
  <div class="mortgage-view-container">
    <MainHeader title="Mortgage Calculator">
      <template #left>
        <font-awesome-icon
          @click="router.back()"
          :icon="['fas', 'arrow-left-long']"
          class="back-arrow pointer"
          role="button"
          aria-label="Go back"
        />
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <div class="ud-section">
        <div class="hero-strip">
          <div class="hero-icon-wrap">
            <font-awesome-icon :icon="['fas', 'house']" />
          </div>
          <div class="hero-text">
            <h2>Mortgage Calculator</h2>
            <p>
              Plan a new mortgage, accelerate an existing one, or compare a
              refinance offer.
            </p>
          </div>
        </div>

        <div class="wrapper-card mode-card">
          <div class="mode-pills">
            <button
              v-for="m in modes"
              :key="m.value"
              type="button"
              :class="['mode-pill', { active: mode === m.value }]"
              @click="setMode(m.value)"
            >
              <font-awesome-icon :icon="m.icon" />
              <span>{{ m.label }}</span>
            </button>
          </div>
          <p class="mode-blurb">{{ activeModeBlurb }}</p>
        </div>

        <template v-if="mode === 'new'">
          <div class="wrapper-card">
            <h3 class="section-label">Loan basics</h3>

            <div class="ud-field">
              <label class="ud-label">Home price</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="newLoan.homePrice"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>

            <div class="ud-field">
              <label class="ud-label">Down payment</label>
              <div class="dual-input">
                <div class="input-with-prefix flex-1">
                  <span class="prefix">$</span>
                  <input
                    v-model.number="newLoan.downDollar"
                    type="number"
                    inputmode="decimal"
                    class="input"
                    @input="onDownDollarInput"
                    placeholder="0"
                  />
                </div>
                <div class="input-with-suffix flex-1">
                  <input
                    v-model.number="newLoan.downPercent"
                    type="number"
                    inputmode="decimal"
                    class="input"
                    @input="onDownPercentInput"
                    placeholder="0"
                    step="0.1"
                  />
                  <span class="suffix">%</span>
                </div>
              </div>
              <p
                v-if="newLoan.homePrice && newLoan.downDollar"
                class="field-hint"
              >
                Loan amount: {{ formatCurrency(newLoanAmount) }}
                <span v-if="newLoanLTV > 80" class="hint-warn">
                  · LTV {{ newLoanLTV.toFixed(1) }}%, PMI applies
                </span>
                <span v-else class="hint-good">
                  · LTV {{ newLoanLTV.toFixed(1) }}%, no PMI
                </span>
              </p>
            </div>

            <div class="dual-input">
              <div class="ud-field flex-1">
                <label class="ud-label">Interest rate</label>
                <div class="input-with-suffix">
                  <input
                    v-model.number="newLoan.rate"
                    type="number"
                    inputmode="decimal"
                    class="input"
                    placeholder="0"
                    step="0.01"
                  />
                  <span class="suffix">%</span>
                </div>
              </div>

              <div class="ud-field flex-1">
                <label class="ud-label">Term</label>
                <div
                  class="form-select grey-outline-form-select"
                  @click="toggleDropdown('newTerm')"
                >
                  <span>{{ newLoan.termYears }} years</span>
                  <font-awesome-icon :icon="['fas', 'chevron-down']" />
                </div>
                <transition name="sub-slide">
                  <div v-if="openDropdown === 'newTerm'" class="dropdown-panel">
                    <button
                      v-for="t in termOptions"
                      :key="t"
                      type="button"
                      class="dropdown-option"
                      :class="{ active: newLoan.termYears === t }"
                      @click="selectNewTerm(t)"
                    >
                      {{ t }} years
                    </button>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <div class="wrapper-card">
            <div class="section-header-row">
              <h3 class="section-label">Taxes, insurance, PMI</h3>
              <button
                type="button"
                class="link-btn"
                @click="newLoan.showPiti = !newLoan.showPiti"
              >
                {{ newLoan.showPiti ? 'Hide' : 'Show' }}
              </button>
            </div>

            <transition name="sub-slide">
              <div v-if="newLoan.showPiti">
                <div class="ud-field">
                  <label class="ud-label">Property tax (annual)</label>
                  <div class="dual-input">
                    <div class="input-with-prefix flex-1">
                      <span class="prefix">$</span>
                      <input
                        v-model.number="newLoan.taxDollar"
                        type="number"
                        inputmode="decimal"
                        class="input"
                        :class="{
                          'input-muted': newLoan.taxMode === 'percent',
                        }"
                        @input="onTaxDollarInput"
                      />
                    </div>
                    <div class="input-with-suffix flex-1">
                      <input
                        v-model.number="newLoan.taxPercent"
                        type="number"
                        inputmode="decimal"
                        class="input"
                        :class="{ 'input-muted': newLoan.taxMode === 'dollar' }"
                        @input="onTaxPercentInput"
                        step="0.01"
                      />
                      <span class="suffix">%</span>
                    </div>
                  </div>
                  <p class="field-hint">
                    US median is around 1.1%. Check your county assessor or
                    Zillow for your area.
                  </p>
                </div>

                <div class="ud-field">
                  <label class="ud-label">Homeowners insurance (annual)</label>
                  <div class="input-with-prefix">
                    <span class="prefix">$</span>
                    <input
                      v-model.number="newLoan.insuranceDollar"
                      type="number"
                      inputmode="decimal"
                      class="input"
                      placeholder="1500"
                    />
                  </div>
                </div>

                <div class="ud-field">
                  <label class="ud-label">HOA (monthly)</label>
                  <div class="input-with-prefix">
                    <span class="prefix">$</span>
                    <input
                      v-model.number="newLoan.hoaMonthly"
                      type="number"
                      inputmode="decimal"
                      class="input"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div class="ud-field">
                  <div class="label-row">
                    <label class="ud-label">PMI rate (annual)</label>
                    <button
                      type="button"
                      class="link-btn small"
                      @click="newLoan.pmiOverride = !newLoan.pmiOverride"
                    >
                      {{ newLoan.pmiOverride ? 'Use auto' : 'Override' }}
                    </button>
                  </div>
                  <div class="input-with-suffix">
                    <input
                      v-model.number="newLoan.pmiPercent"
                      type="number"
                      inputmode="decimal"
                      class="input"
                      :disabled="!newLoan.pmiOverride"
                      step="0.01"
                    />
                    <span class="suffix">%</span>
                  </div>
                  <p class="field-hint">
                    Auto-set to 0.5% when loan-to-value is above 80%, $0
                    otherwise. PMI drops off automatically once you reach 80%
                    LTV through payments.
                  </p>
                </div>
              </div>
            </transition>
          </div>

          <div v-if="newPiti.total > 0" class="wrapper-card affordability-card">
            <h3 class="section-label">Affordability check</h3>

            <div class="ud-field">
              <label class="ud-label"
                >Your monthly gross income (optional)</label
              >
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="newLoan.monthlyIncome"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>

            <div v-if="newLoan.monthlyIncome > 0" class="affordability-readout">
              <div class="afford-row">
                <span class="afford-label">PITI as % of income</span>
                <span class="afford-value" :class="affordabilityClass"
                  >{{ newPitiAsPercentOfIncome.toFixed(1) }}%</span
                >
              </div>

              <div class="ud-field threshold-field">
                <label class="ud-label">Your comfort threshold</label>
                <div class="input-with-suffix">
                  <input
                    v-model.number="newLoan.comfortThreshold"
                    type="number"
                    inputmode="decimal"
                    class="input"
                    step="1"
                  />
                  <span class="suffix">%</span>
                </div>
                <p class="field-hint">
                  Common rules of thumb suggest 28%, but you decide what fits
                  your life. Set your own number based on your other
                  commitments.
                </p>
              </div>

              <div class="afford-verdict" :class="affordabilityClass">
                <font-awesome-icon :icon="affordabilityIcon" />
                <span>{{ affordabilityVerdict }}</span>
              </div>
            </div>
          </div>

          <div v-if="newPiti.total > 0" class="wrapper-card results-card">
            <h3 class="section-label">Monthly payment</h3>

            <div class="big-number">
              {{ formatCurrency(newPiti.total) }}
              <span class="big-number-sub">per month</span>
            </div>

            <div class="piti-breakdown">
              <div class="piti-row">
                <span class="piti-label">Principal &amp; interest</span>
                <span class="piti-value">{{ formatCurrency(newPiti.pi) }}</span>
              </div>
              <div v-if="newPiti.tax > 0" class="piti-row">
                <span class="piti-label">Property tax</span>
                <span class="piti-value">{{
                  formatCurrency(newPiti.tax)
                }}</span>
              </div>
              <div v-if="newPiti.insurance > 0" class="piti-row">
                <span class="piti-label">Insurance</span>
                <span class="piti-value">{{
                  formatCurrency(newPiti.insurance)
                }}</span>
              </div>
              <div v-if="newPiti.pmi > 0" class="piti-row">
                <span class="piti-label">PMI</span>
                <span class="piti-value">{{
                  formatCurrency(newPiti.pmi)
                }}</span>
              </div>
              <div v-if="newPiti.hoa > 0" class="piti-row">
                <span class="piti-label">HOA</span>
                <span class="piti-value">{{
                  formatCurrency(newPiti.hoa)
                }}</span>
              </div>
            </div>

            <div class="result-stats">
              <div class="stat">
                <span class="stat-label">Total interest</span>
                <span class="stat-value">{{
                  formatCurrency(newSchedule.totalInterest)
                }}</span>
              </div>
              <div class="stat">
                <span class="stat-label">Total paid</span>
                <span class="stat-value">{{
                  formatCurrency(newSchedule.totalPaid)
                }}</span>
              </div>
              <div v-if="newSchedule.pmiDropMonth" class="stat">
                <span class="stat-label">PMI drops off</span>
                <span class="stat-value"
                  >Month {{ newSchedule.pmiDropMonth }}</span
                >
              </div>
              <div class="stat">
                <span class="stat-label">Payoff date</span>
                <span class="stat-value">{{ newSchedule.payoffDate }}</span>
              </div>
            </div>

            <button
              type="button"
              class="link-btn full-width"
              @click="newLoan.showSchedule = !newLoan.showSchedule"
            >
              {{ newLoan.showSchedule ? 'Hide' : 'Show' }} amortization schedule
            </button>

            <transition name="sub-slide">
              <div v-if="newLoan.showSchedule" class="schedule-table">
                <div class="schedule-header">
                  <span>Month</span>
                  <span>Principal</span>
                  <span>Interest</span>
                  <span>Balance</span>
                </div>
                <div
                  v-for="row in newSchedule.rows"
                  :key="row.month"
                  class="schedule-row"
                  :class="{ 'pmi-drop-row': row.pmiDropped }"
                >
                  <span>{{ row.month }}</span>
                  <span>{{ formatCurrency(row.principal) }}</span>
                  <span>{{ formatCurrency(row.interest) }}</span>
                  <span>{{ formatCurrency(row.balance) }}</span>
                </div>
              </div>
            </transition>
          </div>
        </template>

        <template v-if="mode === 'payoff'">
          <div class="wrapper-card">
            <h3 class="section-label">Your current mortgage</h3>

            <div class="ud-field">
              <label class="ud-label">Current balance</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="payoff.balance"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>

            <div class="dual-input">
              <div class="ud-field flex-1">
                <label class="ud-label">Interest rate</label>
                <div class="input-with-suffix">
                  <input
                    v-model.number="payoff.rate"
                    type="number"
                    inputmode="decimal"
                    class="input"
                    step="0.01"
                  />
                  <span class="suffix">%</span>
                </div>
              </div>

              <div class="ud-field flex-1">
                <label class="ud-label">Years remaining</label>
                <input
                  v-model.number="payoff.yearsRemaining"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  step="0.5"
                />
              </div>
            </div>

            <div class="ud-field">
              <label class="ud-label"
                >Current monthly payment (P&amp;I only)</label
              >
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="payoff.currentPayment"
                  type="number"
                  inputmode="decimal"
                  class="input"
                />
              </div>
              <p v-if="payoffComputedPayment > 0" class="field-hint">
                Based on your inputs, the payment should be about
                {{ formatCurrency(payoffComputedPayment) }}.
                {{
                  payoff.currentPayment
                    ? 'Use whichever matches your statement.'
                    : 'Tap to use this value.'
                }}
                <button
                  v-if="!payoff.currentPayment"
                  type="button"
                  class="inline-link"
                  @click="
                    payoff.currentPayment = Math.round(payoffComputedPayment)
                  "
                >
                  Use {{ formatCurrency(payoffComputedPayment) }}
                </button>
              </p>
            </div>
          </div>

          <div v-if="payoffWarning" class="wrapper-card warning-card">
            <div class="warn-header">
              <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
              <span>{{ payoffWarning.title }}</span>
            </div>
            <p>{{ payoffWarning.body }}</p>
          </div>

          <div v-if="payoffBaseline" class="wrapper-card">
            <h3 class="section-label">Extra payment strategies</h3>
            <p class="section-blurb">
              Compare ways to pay this off faster. All amounts shown are in
              addition to your current payment.
            </p>

            <div class="ud-field">
              <label class="ud-label">Extra monthly payment</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="payoff.extraMonthly"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>

            <div class="ud-field">
              <label class="ud-label">Annual lump sum</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="payoff.annualLump"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>

            <div class="ud-field">
              <label class="ud-label">One-time payment (now)</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="payoff.oneTime"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>

            <div class="ud-field checkbox-field">
              <label class="checkbox-row">
                <input type="checkbox" v-model="payoff.biweekly" />
                <span
                  >Switch to true biweekly payments (26 half-payments per
                  year)</span
                >
              </label>
              <p class="field-hint">
                A true biweekly schedule means 26 half-payments, which equals 13
                full payments per year instead of 12. Beware: many servicers
                charge fees to enroll, and the same effect is free if you just
                add 1/12 of your payment as extra each month.
              </p>
            </div>

            <div class="ud-field">
              <div class="label-row">
                <label class="ud-label"
                  >Where does the extra come from? (optional)</label
                >
                <button
                  v-if="payoff.extraSourceCategory"
                  type="button"
                  class="link-btn small"
                  @click="payoff.extraSourceCategory = null"
                >
                  Clear
                </button>
              </div>
              <div
                class="form-select grey-outline-form-select"
                @click="toggleDropdown('payoffExtraSource')"
              >
                <span>{{
                  payoff.extraSourceCategory
                    ? payoff.extraSourceCategory.name
                    : 'Pick a budget category'
                }}</span>
                <font-awesome-icon :icon="['fas', 'chevron-down']" />
              </div>
              <transition name="sub-slide">
                <div
                  v-if="openDropdown === 'payoffExtraSource'"
                  class="dropdown-panel"
                >
                  <button
                    v-for="cat in spendingCategories"
                    :key="cat.categoryID"
                    type="button"
                    class="dropdown-option"
                    @click="selectExtraSource(cat)"
                  >
                    <span>{{ cat.name }}</span>
                    <span class="muted-tag"
                      >{{
                        formatCurrency(cat.parentDisplayAvailable)
                      }}
                      left</span
                    >
                  </button>
                </div>
              </transition>
              <p v-if="extraSourceWarning" class="field-hint hint-warn">
                {{ extraSourceWarning }}
              </p>
            </div>

            <div class="strategy-grid">
              <div
                v-for="s in payoffStrategies"
                :key="s.key"
                class="strategy-card"
                :class="{ 'best-strategy': s.isBest }"
              >
                <div class="strategy-header">
                  <h4>{{ s.name }}</h4>
                  <span v-if="s.isBest" class="best-tag">Most savings</span>
                </div>
                <div class="strategy-stats">
                  <div class="strat-row">
                    <span>Payoff in</span>
                    <strong>{{ s.monthsLabel }}</strong>
                  </div>
                  <div class="strat-row">
                    <span>Months saved</span>
                    <strong>{{ s.monthsSaved }}</strong>
                  </div>
                  <div class="strat-row">
                    <span>Interest saved</span>
                    <strong class="saved-amount">{{
                      formatCurrency(s.interestSaved)
                    }}</strong>
                  </div>
                  <div class="strat-row">
                    <span>Total paid</span>
                    <strong>{{ formatCurrency(s.totalPaid) }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div class="baseline-card">
              <p class="baseline-label">Without any extra payments</p>
              <div class="baseline-stats">
                <span>{{ formatMonths(payoffBaseline.months) }}</span>
                <span>·</span>
                <span
                  >{{
                    formatCurrency(payoffBaseline.totalInterest)
                  }}
                  interest</span
                >
              </div>
            </div>
          </div>
        </template>

        <template v-if="mode === 'refi'">
          <div class="wrapper-card">
            <h3 class="section-label">Your current loan</h3>

            <div class="ud-field">
              <label class="ud-label">Current balance</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="refi.currentBalance"
                  type="number"
                  inputmode="decimal"
                  class="input"
                />
              </div>
            </div>

            <div class="dual-input">
              <div class="ud-field flex-1">
                <label class="ud-label">Current rate</label>
                <div class="input-with-suffix">
                  <input
                    v-model.number="refi.currentRate"
                    type="number"
                    inputmode="decimal"
                    class="input"
                    step="0.01"
                  />
                  <span class="suffix">%</span>
                </div>
              </div>

              <div class="ud-field flex-1">
                <label class="ud-label">Years remaining</label>
                <input
                  v-model.number="refi.currentYearsRemaining"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  step="0.5"
                />
              </div>
            </div>
          </div>

          <div class="wrapper-card">
            <h3 class="section-label">New loan offer</h3>

            <div class="dual-input">
              <div class="ud-field flex-1">
                <label class="ud-label">New rate</label>
                <div class="input-with-suffix">
                  <input
                    v-model.number="refi.newRate"
                    type="number"
                    inputmode="decimal"
                    class="input"
                    step="0.01"
                  />
                  <span class="suffix">%</span>
                </div>
              </div>

              <div class="ud-field flex-1">
                <label class="ud-label">New term</label>
                <div
                  class="form-select grey-outline-form-select"
                  @click="toggleDropdown('refiTerm')"
                >
                  <span>{{ refi.newTermYears }} years</span>
                  <font-awesome-icon :icon="['fas', 'chevron-down']" />
                </div>
                <transition name="sub-slide">
                  <div
                    v-if="openDropdown === 'refiTerm'"
                    class="dropdown-panel"
                  >
                    <button
                      v-for="t in termOptions"
                      :key="t"
                      type="button"
                      class="dropdown-option"
                      :class="{ active: refi.newTermYears === t }"
                      @click="selectRefiTerm(t)"
                    >
                      {{ t }} years
                    </button>
                  </div>
                </transition>
              </div>
            </div>

            <div class="ud-field">
              <label class="ud-label">Closing costs</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="refi.closingCosts"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
              <p class="field-hint">
                Typical range is 2 to 6% of the loan amount. This includes
                origination, appraisal, title, and other lender fees.
              </p>
            </div>

            <div class="ud-field checkbox-field">
              <label class="checkbox-row">
                <input type="checkbox" v-model="refi.rollClosingIntoLoan" />
                <span>Roll closing costs into the new loan</span>
              </label>
            </div>
          </div>

          <div v-if="refiResult" class="wrapper-card results-card">
            <h3 class="section-label">Refinance comparison</h3>

            <div class="refi-grid">
              <div class="refi-col">
                <h4 class="refi-col-title">Current loan</h4>
                <div class="refi-stat">
                  <span>Monthly P&amp;I</span>
                  <strong>{{
                    formatCurrency(refiResult.current.payment)
                  }}</strong>
                </div>
                <div class="refi-stat">
                  <span>Remaining interest</span>
                  <strong>{{
                    formatCurrency(refiResult.current.remainingInterest)
                  }}</strong>
                </div>
                <div class="refi-stat">
                  <span>Months left</span>
                  <strong>{{ refiResult.current.months }}</strong>
                </div>
              </div>

              <div class="refi-col refi-col-new">
                <h4 class="refi-col-title">New loan</h4>
                <div class="refi-stat">
                  <span>Monthly P&amp;I</span>
                  <strong>{{
                    formatCurrency(refiResult.newLoan.payment)
                  }}</strong>
                </div>
                <div class="refi-stat">
                  <span>Total interest</span>
                  <strong>{{
                    formatCurrency(refiResult.newLoan.totalInterest)
                  }}</strong>
                </div>
                <div class="refi-stat">
                  <span>Months</span>
                  <strong>{{ refiResult.newLoan.months }}</strong>
                </div>
              </div>
            </div>

            <div class="refi-deltas">
              <div class="delta-row">
                <span>Monthly change</span>
                <strong :class="refiResult.monthlyDelta < 0 ? 'good' : 'bad'">
                  {{ refiResult.monthlyDelta < 0 ? '−' : '+'
                  }}{{ formatCurrency(Math.abs(refiResult.monthlyDelta)) }}
                </strong>
              </div>
              <div class="delta-row">
                <span>Lifetime interest change</span>
                <strong :class="refiResult.interestDelta < 0 ? 'good' : 'bad'">
                  {{ refiResult.interestDelta < 0 ? '−' : '+'
                  }}{{ formatCurrency(Math.abs(refiResult.interestDelta)) }}
                </strong>
              </div>
            </div>

            <div class="break-even-section">
              <h4 class="break-even-title">When does this pay off?</h4>

              <div
                v-if="refiResult.simpleBreakEven !== null"
                class="break-even-row"
              >
                <div class="break-even-label">
                  <span>Simple break-even</span>
                  <small>Closing costs ÷ monthly savings</small>
                </div>
                <strong>{{ formatMonths(refiResult.simpleBreakEven) }}</strong>
              </div>

              <div
                v-if="refiResult.trueBreakEven !== null"
                class="break-even-row primary"
              >
                <div class="break-even-label">
                  <span>True break-even</span>
                  <small
                    >Accounts for your new loan resetting interest-heavy early
                    payments</small
                  >
                </div>
                <strong>{{ formatMonths(refiResult.trueBreakEven) }}</strong>
              </div>

              <div
                v-if="refiResult.simpleBreakEven === null"
                class="break-even-row"
              >
                <p class="hint-warn">
                  Your new payment is higher than your current one, so this
                  refinance does not pay back through monthly savings. It may
                  still make sense if you are shortening the term to save
                  lifetime interest.
                </p>
              </div>
            </div>

            <p class="refi-disclaimer">
              This compares principal and interest only. Real refinance
              decisions also factor in how long you plan to stay in the home,
              whether your tax situation changes, and what you would do with the
              monthly savings.
            </p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';

import MainHeader from '../../components/View-Header.vue';
import { useMyStore } from '../../store';

export default {
  name: 'CalculatorMortgage',
  components: { MainHeader },
  setup() {
    const router = useRouter();
    const store = useMyStore();

    const mode = ref('new');
    const modes = [
      { value: 'new', label: 'New mortgage', icon: ['fas', 'house'] },
      {
        value: 'payoff',
        label: 'Pay off faster',
        icon: ['fas', 'arrow-trend-down'],
      },
      {
        value: 'refi',
        label: 'Refinance',
        icon: ['fas', 'arrow-right-arrow-left'],
      },
    ];

    const setMode = (m) => {
      mode.value = m;
      openDropdown.value = null;
    };

    const activeModeBlurb = computed(() => {
      if (mode.value === 'new')
        return 'Estimate your full monthly payment, including taxes, insurance, and PMI.';
      if (mode.value === 'payoff')
        return 'See how extra payments shorten your loan and save on interest.';
      return 'Compare your current loan to a new offer and find the real break-even point.';
    });

    const openDropdown = ref(null);
    const toggleDropdown = (key) => {
      openDropdown.value = openDropdown.value === key ? null : key;
    };

    const termOptions = [10, 15, 20, 25, 30];

    const newLoan = reactive({
      homePrice: null,
      downDollar: null,
      downPercent: null,
      rate: null,
      termYears: 30,
      showPiti: true,
      taxDollar: null,
      taxPercent: 1.1,
      taxMode: 'percent',
      insuranceDollar: 1500,
      hoaMonthly: 0,
      pmiPercent: 0.5,
      pmiOverride: false,
      monthlyIncome: null,
      comfortThreshold: 28,
      showSchedule: false,
    });

    const onDownDollarInput = () => {
      if (newLoan.homePrice > 0 && newLoan.downDollar >= 0) {
        newLoan.downPercent = +(
          (newLoan.downDollar / newLoan.homePrice) *
          100
        ).toFixed(2);
      }
    };
    const onDownPercentInput = () => {
      if (newLoan.homePrice > 0 && newLoan.downPercent >= 0) {
        newLoan.downDollar = Math.round(
          (newLoan.downPercent / 100) * newLoan.homePrice
        );
      }
    };

    const onTaxDollarInput = () => {
      newLoan.taxMode = 'dollar';
      if (newLoan.homePrice > 0 && newLoan.taxDollar >= 0) {
        newLoan.taxPercent = +(
          (newLoan.taxDollar / newLoan.homePrice) *
          100
        ).toFixed(3);
      }
    };
    const onTaxPercentInput = () => {
      newLoan.taxMode = 'percent';
      if (newLoan.homePrice > 0 && newLoan.taxPercent >= 0) {
        newLoan.taxDollar = Math.round(
          (newLoan.taxPercent / 100) * newLoan.homePrice
        );
      }
    };

    const selectNewTerm = (t) => {
      newLoan.termYears = t;
      openDropdown.value = null;
    };

    const newLoanAmount = computed(() => {
      const price = newLoan.homePrice || 0;
      const down = newLoan.downDollar || 0;
      return Math.max(0, price - down);
    });

    const newLoanLTV = computed(() => {
      if (!newLoan.homePrice || newLoan.homePrice === 0) return 0;
      return (newLoanAmount.value / newLoan.homePrice) * 100;
    });

    const effectivePmiPercent = computed(() => {
      if (newLoan.pmiOverride) return newLoan.pmiPercent || 0;
      return newLoanLTV.value > 80 ? 0.5 : 0;
    });

    const newPiti = computed(() => {
      const loan = newLoanAmount.value;
      const rate = newLoan.rate || 0;
      const term = newLoan.termYears;

      if (loan <= 0 || rate <= 0 || term <= 0) {
        return { pi: 0, tax: 0, insurance: 0, pmi: 0, hoa: 0, total: 0 };
      }

      const monthlyRate = rate / 100 / 12;
      const numPayments = term * 12;
      const pi =
        (loan * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
        (Math.pow(1 + monthlyRate, numPayments) - 1);

      const taxAnnual =
        newLoan.taxMode === 'percent'
          ? ((newLoan.taxPercent || 0) / 100) * (newLoan.homePrice || 0)
          : newLoan.taxDollar || 0;
      const tax = taxAnnual / 12;
      const insurance = (newLoan.insuranceDollar || 0) / 12;
      const pmi = ((effectivePmiPercent.value / 100) * loan) / 12;
      const hoa = newLoan.hoaMonthly || 0;

      return {
        pi: pi || 0,
        tax,
        insurance,
        pmi,
        hoa,
        total: (pi || 0) + tax + insurance + pmi + hoa,
      };
    });

    const newSchedule = computed(() => {
      const loan = newLoanAmount.value;
      const rate = newLoan.rate || 0;
      const term = newLoan.termYears;

      if (loan <= 0 || rate <= 0 || term <= 0) {
        return {
          rows: [],
          totalInterest: 0,
          totalPaid: 0,
          payoffDate: '',
          pmiDropMonth: null,
        };
      }

      const monthlyRate = rate / 100 / 12;
      const numPayments = term * 12;
      const pi =
        (loan * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
        (Math.pow(1 + monthlyRate, numPayments) - 1);

      const homePrice = newLoan.homePrice || loan;
      const pmiDropThreshold = homePrice * 0.8;

      let balance = loan;
      let totalInterest = 0;
      let pmiDropMonth = null;
      const rows = [];

      for (let i = 1; i <= numPayments; i++) {
        const interest = balance * monthlyRate;
        const principal = pi - interest;
        balance = Math.max(0, balance - principal);
        totalInterest += interest;

        let pmiDropped = false;
        if (
          pmiDropMonth === null &&
          balance <= pmiDropThreshold &&
          effectivePmiPercent.value > 0
        ) {
          pmiDropMonth = i;
          pmiDropped = true;
        }

        rows.push({
          month: i,
          principal: principal,
          interest: interest,
          balance: balance,
          pmiDropped,
        });

        if (balance <= 0.01) break;
      }

      const start = new Date();
      const end = new Date(
        start.getFullYear(),
        start.getMonth() + numPayments,
        start.getDate()
      );
      const payoffDate = end.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });

      return {
        rows,
        totalInterest,
        totalPaid: loan + totalInterest,
        payoffDate,
        pmiDropMonth,
      };
    });

    const newPitiAsPercentOfIncome = computed(() => {
      if (!newLoan.monthlyIncome || newLoan.monthlyIncome === 0) return 0;
      return (newPiti.value.total / newLoan.monthlyIncome) * 100;
    });

    const affordabilityClass = computed(() => {
      const pct = newPitiAsPercentOfIncome.value;
      const threshold = newLoan.comfortThreshold || 28;
      if (pct === 0) return 'neutral';
      if (pct <= threshold) return 'good';
      if (pct <= threshold + 5) return 'caution';
      return 'bad';
    });

    const affordabilityVerdict = computed(() => {
      const pct = newPitiAsPercentOfIncome.value;
      const threshold = newLoan.comfortThreshold || 28;
      if (pct === 0) return 'Add your income to see how this fits.';
      if (pct <= threshold)
        return `Within your ${threshold}% threshold. Comfortable.`;
      if (pct <= threshold + 5)
        return `Above your ${threshold}% threshold but close. Worth a careful look at your other commitments.`;
      return `Well above your ${threshold}% threshold. This stretches your budget.`;
    });

    const affordabilityIcon = computed(() => {
      if (affordabilityClass.value === 'good') return ['fas', 'circle-check'];
      if (affordabilityClass.value === 'bad')
        return ['fas', 'circle-exclamation'];
      return ['fas', 'calculator'];
    });

    const payoff = reactive({
      balance: null,
      rate: null,
      yearsRemaining: null,
      currentPayment: null,
      extraMonthly: 0,
      annualLump: 0,
      oneTime: 0,
      biweekly: false,
      extraSourceCategory: null,
    });

    const payoffComputedPayment = computed(() => {
      const bal = payoff.balance || 0;
      const rate = payoff.rate || 0;
      const years = payoff.yearsRemaining || 0;
      if (bal <= 0 || rate <= 0 || years <= 0) return 0;
      const r = rate / 100 / 12;
      const n = years * 12;
      return (bal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    });

    function simulatePayoff({
      balance,
      rate,
      payment,
      extraMonthly = 0,
      annualLump = 0,
      oneTime = 0,
      biweekly = false,
      maxMonths = 600,
    }) {
      if (balance <= 0 || rate <= 0 || payment <= 0) {
        return {
          months: 0,
          totalInterest: 0,
          totalPaid: 0,
          neverPaysOff: false,
        };
      }

      const monthlyRate = rate / 100 / 12;

      const firstMonthInterest = balance * monthlyRate;
      if (
        payment <= firstMonthInterest &&
        extraMonthly === 0 &&
        annualLump === 0 &&
        oneTime === 0 &&
        !biweekly
      ) {
        return {
          months: Infinity,
          totalInterest: Infinity,
          totalPaid: Infinity,
          neverPaysOff: true,
        };
      }

      let bal = balance - (oneTime || 0);
      if (bal <= 0) {
        return {
          months: 1,
          totalInterest: 0,
          totalPaid: oneTime,
          neverPaysOff: false,
        };
      }

      let totalInterest = 0;
      let totalPaid = oneTime || 0;
      let months = 0;

      const biweeklyExtra = biweekly ? payment / 12 : 0;

      for (let i = 1; i <= maxMonths; i++) {
        const interest = bal * monthlyRate;
        let monthPayment = payment + (extraMonthly || 0) + biweeklyExtra;

        if (annualLump > 0 && i % 12 === 0) {
          monthPayment += annualLump;
        }

        const principal = monthPayment - interest;
        if (principal <= 0) {
          return {
            months: Infinity,
            totalInterest: Infinity,
            totalPaid: Infinity,
            neverPaysOff: true,
          };
        }

        if (principal >= bal) {
          totalInterest += interest;
          totalPaid += bal + interest;
          months = i;
          bal = 0;
          break;
        }

        bal -= principal;
        totalInterest += interest;
        totalPaid += monthPayment;
        months = i;
      }

      if (bal > 0.01) {
        return {
          months: Infinity,
          totalInterest: Infinity,
          totalPaid: Infinity,
          neverPaysOff: true,
        };
      }

      return { months, totalInterest, totalPaid, neverPaysOff: false };
    }

    const payoffBaseline = computed(() => {
      const payment = payoff.currentPayment || payoffComputedPayment.value;
      if (!payoff.balance || !payoff.rate || !payment) return null;
      return simulatePayoff({
        balance: payoff.balance,
        rate: payoff.rate,
        payment,
      });
    });

    const payoffWarning = computed(() => {
      if (!payoffBaseline.value) return null;
      if (payoffBaseline.value.neverPaysOff) {
        return {
          title: 'Your payment may not cover the interest',
          body: 'At this rate and balance, your monthly payment is at or below the interest accruing each month. Without extra payments, the loan will not pay off. Double-check your inputs against your statement.',
        };
      }
      const payment = payoff.currentPayment || payoffComputedPayment.value;
      const monthlyInterest =
        (payoff.balance || 0) * ((payoff.rate || 0) / 100 / 12);
      if (payment > 0 && monthlyInterest / payment > 0.85) {
        return {
          title: 'Most of your payment is going to interest',
          body: `About ${((monthlyInterest / payment) * 100).toFixed(0)}% of your current payment is interest. Even a small extra amount makes a meaningful dent here.`,
        };
      }
      return null;
    });

    const payoffStrategies = computed(() => {
      if (!payoffBaseline.value || payoffBaseline.value.neverPaysOff) return [];
      const baseline = payoffBaseline.value;
      const payment = payoff.currentPayment || payoffComputedPayment.value;

      const strategies = [];

      const combined = simulatePayoff({
        balance: payoff.balance,
        rate: payoff.rate,
        payment,
        extraMonthly: payoff.extraMonthly || 0,
        annualLump: payoff.annualLump || 0,
        oneTime: payoff.oneTime || 0,
        biweekly: payoff.biweekly,
      });

      const hasAnyExtra =
        payoff.extraMonthly > 0 ||
        payoff.annualLump > 0 ||
        payoff.oneTime > 0 ||
        payoff.biweekly;

      if (hasAnyExtra) {
        strategies.push({
          key: 'combined',
          name: 'Your plan',
          ...buildStrategyDisplay(combined, baseline),
        });
      }

      if (payoff.extraMonthly > 0) {
        const r = simulatePayoff({
          balance: payoff.balance,
          rate: payoff.rate,
          payment,
          extraMonthly: payoff.extraMonthly,
        });
        strategies.push({
          key: 'monthly',
          name: `+${formatCurrency(payoff.extraMonthly)}/mo`,
          ...buildStrategyDisplay(r, baseline),
        });
      }

      if (payoff.annualLump > 0) {
        const r = simulatePayoff({
          balance: payoff.balance,
          rate: payoff.rate,
          payment,
          annualLump: payoff.annualLump,
        });
        strategies.push({
          key: 'annual',
          name: `${formatCurrency(payoff.annualLump)}/yr lump`,
          ...buildStrategyDisplay(r, baseline),
        });
      }

      if (payoff.oneTime > 0) {
        const r = simulatePayoff({
          balance: payoff.balance,
          rate: payoff.rate,
          payment,
          oneTime: payoff.oneTime,
        });
        strategies.push({
          key: 'onetime',
          name: `${formatCurrency(payoff.oneTime)} one-time`,
          ...buildStrategyDisplay(r, baseline),
        });
      }

      if (payoff.biweekly) {
        const r = simulatePayoff({
          balance: payoff.balance,
          rate: payoff.rate,
          payment,
          biweekly: true,
        });
        strategies.push({
          key: 'biweekly',
          name: 'Biweekly payments',
          ...buildStrategyDisplay(r, baseline),
        });
      }

      if (strategies.length > 0) {
        const best = strategies.reduce((a, b) =>
          a.interestSaved > b.interestSaved ? a : b
        );
        strategies.forEach((s) => {
          s.isBest = s.key === best.key;
        });
      }

      return strategies;
    });

    function buildStrategyDisplay(result, baseline) {
      if (result.neverPaysOff) {
        return {
          monthsLabel: 'Never',
          monthsSaved: 0,
          interestSaved: 0,
          totalPaid: 0,
          isBest: false,
        };
      }
      return {
        monthsLabel: formatMonths(result.months),
        monthsSaved: Math.max(0, baseline.months - result.months),
        interestSaved: Math.max(
          0,
          baseline.totalInterest - result.totalInterest
        ),
        totalPaid: result.totalPaid,
        isBest: false,
      };
    }

    const allCategories = ref([]);
    const spendingCategories = computed(() => {
      return allCategories.value
        .filter((c) => !c.income)
        .map((c) => ({
          ...c,
          parentDisplayAvailable:
            c.subcategories && c.subcategories.length > 0
              ? c.incSubAvailable
              : c.available,
        }));
    });

    const selectExtraSource = (cat) => {
      payoff.extraSourceCategory = cat;
      openDropdown.value = null;
    };

    const extraSourceWarning = computed(() => {
      if (!payoff.extraSourceCategory) return null;
      const totalExtra =
        (payoff.extraMonthly || 0) +
        (payoff.annualLump || 0) / 12 +
        (payoff.biweekly ? (payoff.currentPayment || 0) / 12 : 0);
      const available = payoff.extraSourceCategory.parentDisplayAvailable || 0;
      if (totalExtra > available) {
        return `Your extra payments average ${formatCurrency(totalExtra)}/month, but ${payoff.extraSourceCategory.name} only has ${formatCurrency(available)} available this month.`;
      }
      return null;
    });

    const refi = reactive({
      currentBalance: null,
      currentRate: null,
      currentYearsRemaining: null,
      newRate: null,
      newTermYears: 30,
      closingCosts: 0,
      rollClosingIntoLoan: false,
    });

    const selectRefiTerm = (t) => {
      refi.newTermYears = t;
      openDropdown.value = null;
    };

    function calcPayment(balance, rate, years) {
      if (balance <= 0 || rate <= 0 || years <= 0) return 0;
      const r = rate / 100 / 12;
      const n = years * 12;
      return (balance * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    }

    function totalInterestForLoan(balance, rate, years) {
      const payment = calcPayment(balance, rate, years);
      return payment * years * 12 - balance;
    }

    const refiResult = computed(() => {
      const cb = refi.currentBalance || 0;
      const cr = refi.currentRate || 0;
      const cy = refi.currentYearsRemaining || 0;
      const nr = refi.newRate || 0;
      const nt = refi.newTermYears;
      const closing = refi.closingCosts || 0;

      if (cb <= 0 || cr <= 0 || cy <= 0 || nr <= 0 || nt <= 0) return null;

      const currentPayment = calcPayment(cb, cr, cy);
      const currentRemainingInterest = currentPayment * cy * 12 - cb;

      const newLoanBalance = refi.rollClosingIntoLoan ? cb + closing : cb;
      const newPayment = calcPayment(newLoanBalance, nr, nt);
      const newTotalInterest = totalInterestForLoan(newLoanBalance, nr, nt);

      const monthlyDelta = newPayment - currentPayment;
      const totalCostCurrent = cb + currentRemainingInterest;
      const totalCostNew =
        newLoanBalance +
        newTotalInterest +
        (refi.rollClosingIntoLoan ? 0 : closing);
      const interestDelta = totalCostNew - totalCostCurrent;

      let simpleBreakEven = null;
      if (monthlyDelta < 0 && closing > 0) {
        simpleBreakEven = Math.ceil(closing / Math.abs(monthlyDelta));
      } else if (monthlyDelta < 0 && closing === 0) {
        simpleBreakEven = 0;
      }

      let trueBreakEven = null;
      if (monthlyDelta <= 0 || nr < cr) {
        trueBreakEven = computeTrueBreakEven(
          cb,
          cr,
          cy,
          newLoanBalance,
          nr,
          nt,
          refi.rollClosingIntoLoan ? 0 : closing
        );
      }

      return {
        current: {
          payment: currentPayment,
          remainingInterest: currentRemainingInterest,
          months: cy * 12,
        },
        newLoan: {
          payment: newPayment,
          totalInterest: newTotalInterest,
          months: nt * 12,
        },
        monthlyDelta,
        interestDelta,
        simpleBreakEven,
        trueBreakEven,
      };
    });

    function computeTrueBreakEven(
      curBal,
      curRate,
      curYears,
      newBal,
      newRate,
      newYears,
      upfrontCost
    ) {
      const curR = curRate / 100 / 12;
      const newR = newRate / 100 / 12;
      const curPayment = calcPayment(curBal, curRate, curYears);
      const newPayment = calcPayment(newBal, newRate, newYears);

      let curBalance = curBal;
      let newBalance = newBal;
      let curCumInterest = 0;
      let newCumInterest = upfrontCost;

      const maxMonths = Math.min(curYears * 12, newYears * 12, 600);

      for (let i = 1; i <= maxMonths; i++) {
        const curInterest = curBalance * curR;
        const curPrincipal = curPayment - curInterest;
        curBalance = Math.max(0, curBalance - curPrincipal);
        curCumInterest += curInterest;

        const newInterest = newBalance * newR;
        const newPrincipal = newPayment - newInterest;
        newBalance = Math.max(0, newBalance - newPrincipal);
        newCumInterest += newInterest;

        const curTotalSpent = curBal - curBalance + curCumInterest;
        const newTotalSpent = newBal - newBalance + newCumInterest;

        if (newTotalSpent <= curTotalSpent) {
          return i;
        }
      }
      return null;
    }

    function formatCurrency(n) {
      if (!isFinite(n) || n === null || n === undefined) return '$0';
      return '$' + Math.round(n).toLocaleString('en-US');
    }

    function formatMonths(m) {
      if (!isFinite(m) || m === Infinity) return 'Never';
      if (m < 12) return `${m} mo`;
      const years = Math.floor(m / 12);
      const months = m % 12;
      if (months === 0) return `${years} yr`;
      return `${years} yr ${months} mo`;
    }

    onMounted(async () => {
      try {
        const now = new Date();
        const response = await store.getMonthlyBudgetDataForAllCategories(
          now.getMonth() + 1,
          now.getFullYear()
        );
        if (response && response.data && response.data.categoryBudgets) {
          allCategories.value = response.data.categoryBudgets;
        }
      } catch (e) {
        allCategories.value = [];
      }
    });

    return {
      router,
      mode,
      modes,
      setMode,
      activeModeBlurb,
      openDropdown,
      toggleDropdown,
      termOptions,
      newLoan,
      newLoanAmount,
      newLoanLTV,
      newPiti,
      newSchedule,
      onDownDollarInput,
      onDownPercentInput,
      onTaxDollarInput,
      onTaxPercentInput,
      selectNewTerm,
      newPitiAsPercentOfIncome,
      affordabilityClass,
      affordabilityVerdict,
      affordabilityIcon,
      payoff,
      payoffComputedPayment,
      payoffBaseline,
      payoffWarning,
      payoffStrategies,
      spendingCategories,
      selectExtraSource,
      extraSourceWarning,
      refi,
      selectRefiTerm,
      refiResult,
      formatCurrency,
      formatMonths,
    };
  },
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}

.wrapper-card {
  margin-bottom: 16px;
}

.ud-field {
  margin-top: 1em;
}

.back-arrow {
  font-size: 18px;
  color: var(--color-primary);
  cursor: pointer;
  transition: transform var(--transition-fast);
}
.back-arrow:hover {
  transform: translateX(-2px);
}
.dark-theme .back-arrow {
  color: var(--color-accent-teal);
}

.hero-icon-wrap font-awesome-icon {
  color: white;
  font-size: 24px;
}

.mode-pill font-awesome-icon {
  font-size: 18px;
}

.afford-verdict font-awesome-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.warn-header font-awesome-icon {
  font-size: 20px;
}

.ud-section {
  padding: 16px;
  padding-bottom: 80px;
}

.hero-strip {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(
    135deg,
    var(--purple-50) 0%,
    var(--teal-50, #e5f7f1) 100%
  );
  border-radius: var(--radius-lg);
  margin-bottom: 16px;
}
.hero-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  background: var(--color-accent-purple);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hero-text h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  margin: 0 0 4px 0;
}
.hero-text p {
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

.mode-card {
  margin-bottom: 16px;
}
.mode-pills {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.mode-pill {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 8px;
  border: 1.5px solid #e5e5e5;
  background: white;
  border-radius: var(--radius-md);
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-pill.active {
  background: var(--color-deep-teal, #004751);
  color: white;
  border-color: var(--color-deep-teal, #004751);
  box-shadow: var(--shadow-sm);
}
.mode-blurb {
  font-size: 13px;
  margin: 0;
  line-height: 1.4;
}

.light-theme .mode-blurb {
  color: #666;
}

.section-label {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
}
.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.section-blurb {
  font-size: 13px;
  color: #666;
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.input {
  width: 100%;
  box-sizing: border-box;
}

.input-with-prefix,
.input-with-suffix {
  position: relative;
  display: flex;
  align-items: center;
}
.input-with-prefix .input {
  padding-left: 28px;
}
.input-with-suffix .input {
  padding-right: 32px;
}
.prefix,
.suffix {
  position: absolute;
  color: #999;
  font-size: 14px;
  pointer-events: none;
}
.prefix {
  left: 12px;
}
.suffix {
  right: 12px;
}

.input-muted {
  color: #999;
}

.dual-input {
  display: flex;
  gap: 10px;
}
.flex-1 {
  flex: 1;
}

.label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.field-hint {
  font-size: 12px;
  margin: 6px 0 0 0;
  line-height: 1.4;
}
.light-theme .field-hint {
  color: #888;
}
.hint-warn {
  color: #c97b2c;
}
.hint-good {
  color: var(--color-accent-green, #5dcaa5);
}

.inline-link {
  background: none;
  border: none;
  color: var(--color-accent-purple);
  text-decoration: underline;
  font-size: 12px;
  padding: 0 0 0 4px;
  cursor: pointer;
  font-family: inherit;
}

.link-btn {
  background: none;
  border: none;
  color: var(--color-accent-purple);
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
}
.link-btn.small {
  font-size: 12px;
}
.link-btn.full-width {
  width: 100%;
  text-align: center;
  padding: 10px;
  margin-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.dropdown-panel {
  margin-top: 6px;
  border: 1px solid #e5e5e5;
  border-radius: var(--radius-md);
  background: white;
  overflow: hidden;
}
.dropdown-option {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  background: white;
  border: none;
  border-bottom: 1px solid #f5f5f5;
  text-align: left;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  cursor: pointer;
}
.dropdown-option:last-child {
  border-bottom: none;
}
.dropdown-option:hover {
  background: var(--purple-50);
}
.dropdown-option.active {
  background: var(--purple-50);
  color: var(--color-accent-purple);
  font-weight: 500;
}
.muted-tag {
  font-size: 12px;
  color: #888;
}

.checkbox-field {
  padding: 8px 0;
}
.checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  cursor: pointer;
  line-height: 1.4;
}
.checkbox-row input {
  margin-top: 2px;
}

.affordability-card {
  background: var(--purple-50);
}
.affordability-readout {
  margin-top: 12px;
}
.afford-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
}
.afford-label {
  font-size: 14px;
  color: #555;
}
.afford-value {
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
}
.afford-value.good {
  color: var(--color-accent-green, #5dcaa5);
}
.afford-value.caution {
  color: #e2a53c;
}
.afford-value.bad {
  color: #d45a4a;
}
.afford-value.neutral {
  color: #888;
}

.threshold-field {
  margin-top: 8px;
  margin-bottom: 8px;
}

.afford-verdict {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: var(--radius-md);
  font-size: 14px;
  margin-top: 12px;
  background: white;
  line-height: 1.4;
}

.afford-verdict.good {
  color: var(--color-accent-green, #5dcaa5);
  border-left: 3px solid var(--color-accent-green);
}
.afford-verdict.caution {
  color: #e2a53c;
  border-left: 3px solid #e2a53c;
}
.afford-verdict.bad {
  color: #d45a4a;
  border-left: 3px solid #d45a4a;
}
.afford-verdict.neutral {
  color: #888;
}

.results-card {
  background: white;
}
.big-number {
  font-family: 'Poppins', sans-serif;
  font-size: 32px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
}
.big-number-sub {
  font-size: 13px;
  font-weight: 400;
  color: #888;
}

.piti-breakdown {
  background: var(--purple-50);
  border-radius: var(--radius-md);
  padding: 12px 14px;
  margin-bottom: 16px;
}
.piti-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}
.piti-label {
  color: #555;
}
.piti-value {
  font-weight: 500;
  color: var(--color-deep-teal, #004751);
}

.result-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}
.stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  background: var(--teal-50, #e5f7f1);
  border-radius: var(--radius-sm);
}
.stat-label {
  font-size: 11px;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.stat-value {
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
}

.schedule-table {
  margin-top: 12px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: var(--radius-sm);
}
.schedule-header,
.schedule-row {
  display: grid;
  grid-template-columns: 60px 1fr 1fr 1fr;
  font-size: 12px;
  padding: 8px 10px;
  border-bottom: 1px solid #f5f5f5;
}
.schedule-header {
  background: var(--purple-50);
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  position: sticky;
  top: 0;
}
.schedule-row.pmi-drop-row {
  background: var(--teal-50, #e5f7f1);
  font-weight: 500;
}

.warning-card {
  background: #fff8ee;
  border-left: 3px solid #e2a53c;
}
.warn-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  color: #c97b2c;
  margin-bottom: 6px;
}

.warning-card p {
  margin: 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.strategy-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-top: 12px;
  margin-bottom: 12px;
}
@media (min-width: 600px) {
  .strategy-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.strategy-card {
  background: white;
  border: 1.5px solid #e5e5e5;
  border-radius: var(--radius-md);
  padding: 14px;
  transition: all 0.2s ease;
}
.strategy-card.best-strategy {
  border-color: var(--color-accent-green, #5dcaa5);
  box-shadow: 0 0 0 3px rgba(93, 202, 165, 0.1);
}
.strategy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.strategy-header h4 {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  margin: 0;
}
.best-tag {
  background: var(--color-accent-green, #5dcaa5);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: var(--radius-pill);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.strategy-stats {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.strat-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}
.strat-row span {
  color: #666;
}
.strat-row strong {
  color: var(--color-deep-teal, #004751);
  font-weight: 500;
}
.saved-amount {
  color: var(--color-accent-green, #5dcaa5) !important;
}

.baseline-card {
  background: #f8f8f8;
  border-radius: var(--radius-md);
  padding: 12px;
  text-align: center;
}
.baseline-label {
  font-size: 12px;
  color: #888;
  margin: 0 0 4px 0;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.baseline-stats {
  display: flex;
  justify-content: center;
  gap: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #555;
}

.refi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}
.refi-col {
  background: #f8f8f8;
  border-radius: var(--radius-md);
  padding: 12px;
}
.refi-col-new {
  background: var(--purple-50);
}
.refi-col-title {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 10px 0;
}
.refi-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}
.refi-stat span {
  font-size: 11px;
  color: #888;
}
.refi-stat strong {
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
}

.refi-deltas {
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: var(--radius-md);
  padding: 12px;
  margin-bottom: 16px;
}
.delta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 14px;
}
.delta-row span {
  color: #555;
}
.delta-row strong {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
}
.delta-row strong.good {
  color: var(--color-accent-green, #5dcaa5);
}
.delta-row strong.bad {
  color: #d45a4a;
}

.break-even-section {
  background: var(--teal-50, #e5f7f1);
  border-radius: var(--radius-md);
  padding: 14px;
  margin-bottom: 12px;
}
.break-even-title {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  margin: 0 0 12px 0;
}
.break-even-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 71, 81, 0.08);
}
.break-even-row:last-child {
  border-bottom: none;
}
.break-even-row.primary strong {
  color: var(--color-accent-purple);
  font-size: 18px;
}
.break-even-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}
.break-even-label span {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-deep-teal, #004751);
}
.break-even-label small {
  font-size: 11px;
  color: #888;
  line-height: 1.3;
}
.break-even-row strong {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
}

.refi-disclaimer {
  font-size: 12px;
  color: #888;
  line-height: 1.5;
  margin: 0;
}
</style>
